import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Layers, Trash2 } from "lucide-react";
import {
  getListCategoriesV1RankCategoriesGetQueryKey,
  getListKeywordsV1RankKeywordsGetQueryKey,
  useCreateKeywordV1RankKeywordsPost,
  useDeleteKeywordV1RankKeywordsKeywordDelete,
  useListCategoriesV1RankCategoriesGet,
  useListKeywordsV1RankKeywordsGet,
  useUpdateCategoryV1RankCategoriesPlatformCategoryIdPatch,
  useUpdateKeywordV1RankKeywordsKeywordPatch,
} from "../api/generated/qcsControlPlane";
import type { CategoryOut, KeywordOut, ShelfHealth } from "../api/generated/model";
import { Flow } from "../api/generated/model";
import { ago } from "../api/labels";
import { useCanWrite } from "../auth/me";
import { platformKeysFor, platformLabel } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { useToast } from "../components/toast";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  EmptyState,
  Input,
  Panel,
  PageHeader,
  Row,
  Switch,
} from "@akaike/ui";
import { Seg } from "../components/foundation";
import { RankPlatformMeasurement } from "../components/RankPlatformMeasurement";
import { useSessionSelection } from "../lib/useSessionSelection";

// Stable empty fallbacks: `query.data ?? []` mints a new array on every render, which the
// table's memos would read as new data each pass.
const NO_KEYWORDS: KeywordOut[] = [];
const NO_CATEGORIES: CategoryOut[] = [];
const TRACKING_FILTERS = [
  { value: "", label: "All" },
  { value: "tracked", label: "Tracked" },
  { value: "off", label: "Untracked" },
];

// Sort value for a shelf no run has measured. Below zero so those rows sort under a real
// zero, which is a different statement: nobody asked, versus asked and got nothing.
const NEVER = -1;

function errMsg(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    const m = (error as { message?: unknown }).message;
    if (typeof m === "string" && m) return m;
  }
  return fallback;
}

/**
 * How the last run's probes went, as one cell.
 *
 * "Answered" is deliberately not "succeeded": a shelf the platform said was empty is a
 * measurement, and what this separates out is the shelf nobody could reach. The two badges
 * are the readings an operator acts on -- probes that never came back, and a term that
 * answered everywhere and returned nothing, which is almost always a typo.
 */
function Probes({ health }: { health?: ShelfHealth | null }) {
  if (!health) return <span className="text-xs text-ink-2">Not measured</span>;
  const { probes, answered, placements } = health;
  const missed = probes - answered;
  return (
    <Row className="items-center gap-2">
      <span className="num">
        {answered.toLocaleString("en-IN")} / {probes.toLocaleString("en-IN")}
      </span>
      {missed > 0 ? <Badge variant="outline-warning">{missed} unanswered</Badge> : null}
      {answered > 0 && placements === 0 ? <Badge variant="outline-warning">no results</Badge> : null}
    </Row>
  );
}

/**
 * The key form of a typed term, mirroring the backend's `normalise_keyword`.
 *
 * Only used to decide whether the stored key is worth showing beside what somebody typed:
 * case and spacing do not fork a series, so a key that differs from the display by those
 * alone is not news. The server is what actually keys the row.
 */
function normaliseKeyword(raw: string): string {
  return raw.toLowerCase().split(/\s+/).filter(Boolean).join(" ");
}

function Count({ value }: { value: number }) {
  return value === NEVER ? <span className="text-xs text-ink-2">-</span> : <span className="num">{value.toLocaleString("en-IN")}</span>;
}

function Measured({ at }: { at: string }) {
  return at ? <span title={at}>{ago(at)}</span> : <span className="text-xs text-ink-2">-</span>;
}

export function Rank() {
  const canWrite = useCanWrite();
  const notify = useToast();
  const qc = useQueryClient();
  // Two platform lists, because the two universes are not the same shape. Keywords are one
  // list for every platform that can measure a search shelf, and each platform reads its
  // own columns. Categories are addressed by a platform's own id, so that panel picks one
  // platform at a time.
  const searchPlatforms = platformKeysFor(Flow.search_rank);
  const categoryPlatforms = platformKeysFor(Flow.category_rank);
  // ``undefined`` when this deployment has no platform that can measure a category shelf.
  // The query is gated on it rather than sent with a missing parameter, which the API
  // answers 422 -- so the panel shows its "nothing tracked" empty state instead of an
  // error about a request nobody could have made.
  const [platform, setPlatform] = useSessionSelection("qcs.rank.category-platform", categoryPlatforms, categoryPlatforms[0]);
  const [typed, setTyped] = useState("");
  const [adding, setAdding] = useState(false);

  const keywords = useListKeywordsV1RankKeywordsGet();
  const categories = useListCategoriesV1RankCategoriesGet(
    { platform },
    { query: { enabled: platform !== undefined } },
  );

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: getListKeywordsV1RankKeywordsGetQueryKey() });
    void qc.invalidateQueries({
      queryKey: getListCategoriesV1RankCategoriesGetQueryKey({ platform }),
    });
  };

  const create = useCreateKeywordV1RankKeywordsPost({
    mutation: {
      onSuccess: (row) => {
        setTyped("");
        setAdding(false);
        refresh();
        notify(`Added "${row.display}" - switch Tracked on to measure it`);
      },
      onError: (e) => notify(errMsg(e, "Could not add the keyword"), "crit"),
    },
  });
  const setKeyword = useUpdateKeywordV1RankKeywordsKeywordPatch({
    mutation: {
      onSuccess: refresh,
      onError: (e) => notify(errMsg(e, "Could not update the keyword"), "crit"),
    },
  });
  // Deleting is for the term nobody meant to type. The Tracked switch is what stops a term
  // whose series is worth keeping, and the confirm says so, because the two gestures look
  // identical from the table and only one of them is reversible.
  const removeKeyword = useDeleteKeywordV1RankKeywordsKeywordDelete({
    mutation: {
      onSuccess: () => {
        refresh();
        notify("Keyword deleted - the placements already measured under it are untouched");
      },
      onError: (e) => notify(errMsg(e, "Could not delete the keyword"), "crit"),
    },
  });
  const setCategory = useUpdateCategoryV1RankCategoriesPlatformCategoryIdPatch({
    mutation: {
      onSuccess: refresh,
      onError: (e) => notify(errMsg(e, "Could not update the category"), "crit"),
    },
  });

  const add = () => {
    const keyword = typed.trim();
    if (keyword && !create.isPending) create.mutate({ data: { keyword } });
  };

  const keywordRows = keywords.data ?? NO_KEYWORDS;
  // Alphabetical by name path, so the shelves of one top-level category sit together: a
  // platform ships hundreds and the id order scatters siblings across the whole table.
  const categoryRows = useMemo(
    () => [...(categories.data ?? NO_CATEGORIES)].sort((a, b) => a.label.localeCompare(b.label)),
    [categories.data],
  );

  // Keywords are shared across platforms; keep their measurements together in one cell.
  const keywordColumns = useMemo<AnyColumnDef<KeywordOut>[]>(() => {
    const col = createColumnHelper<KeywordOut>();
    const reading = (row: KeywordOut, p: string): ShelfHealth | undefined => (row.health ?? {})[p];
    return [
      // One column, not two. The stored key is derived from what somebody typed, so for
      // almost every term the two forms are the same string and a second column just
      // repeated it. The key appears under the typed form only when it is genuinely a
      // different string, which is the one case where "which form is this filed under"
      // is a real question. Search still matches either form.
      col.accessor("display", {
        header: "Keyword",
        cell: ({ row }) => {
          const { display, keyword } = row.original;
          const to = `/rank/keywords/${encodeURIComponent(keyword)}`;
          return (
            <Link
              className="flex h-full w-full flex-col justify-center leading-tight [&>span]:truncate text-ink no-underline hover:text-brand hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand"
              to={to}
              title={display}
            >
              <span>{display}</span>
              {normaliseKeyword(display) !== keyword && (
                <code className="text-xs text-ink-2" title="The key every share query filters on">
                  filed as {keyword}
                </code>
              )}
            </Link>
          );
        },
        meta: {
          cellClassName: "strong",
          width: 240,
          flex: false,
          searchValue: (row) => `${row.display} ${row.keyword}`,
        },
      }),
      col.accessor((row) => (row.enabled ? "tracked" : "off"), {
        id: "enabled",
        header: "Tracked",
        enableSorting: false,
        cell: ({ row }) => (
          <Switch
            aria-label={`Track ${row.original.display}`}
            checked={row.original.enabled}
            disabled={!canWrite || setKeyword.isPending}
            onCheckedChange={(enabled) =>
              setKeyword.mutate({
                // The generated client puts the keyword straight in the path, and a term the
                // registry normalised keeps its punctuation: unencoded, a `?` or a `#`
                // truncates the path and the switch flips nothing.
                keyword: encodeURIComponent(row.original.keyword),
                data: { enabled },
              })
            }
          />
        ),
        meta: { align: "center", width: 80, flex: false, filterSelect: true, filterLabel: "keyword tracking", filterSegments: TRACKING_FILTERS },
      }),
      ...searchPlatforms.map((p) => col.display({
        id: `${p} measurements`,
        header: () => <span className={`plat ${p}`}>{platformLabel(p)}</span>,
        cell: ({ row }) => (
          <RankPlatformMeasurement
            platform={p}
            keyword={row.original.keyword}
            health={reading(row.original, p)}
          />
        ),
        meta: {
          width: 240,
          flex: false,
          exportValue: (row) => {
            const health = reading(row, p);
            return health
              ? `${health.placements} placements; ${health.answered}/${health.probes} probes answered; ${health.probes - health.answered} unanswered; measured ${health.last_measured_at || "unknown"}`
              : "Not measured";
          },
        },
        enableSorting: false,
      })),
      ...(canWrite ? [col.display({
        id: "delete",
        header: "Actions",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Delete ${row.original.display}`}
            title={`Delete ${row.original.display}`}
            disabled={removeKeyword.isPending}
            onClick={() => {
              const term = row.original.display;
              const ok = confirm(
                `Delete "${term}"? The placements already measured under it stay, but the term ` +
                  "leaves the list for good. To stop measuring one you may want back, " +
                  "switch Tracked off instead.",
              );
              if (ok)
                removeKeyword.mutate({ keyword: encodeURIComponent(row.original.keyword) });
            }}
          >
            <Trash2 size={14} aria-hidden="true" />
          </Button>
        ),
        meta: { align: "center", width: 84 },
        enableSorting: false,
      })] : []),
    ];
  }, [canWrite, searchPlatforms, setKeyword, removeKeyword]);

  const categoryColumns = useMemo<AnyColumnDef<CategoryOut>[]>(() => {
    const col = createColumnHelper<CategoryOut>();
    return [
      // The full name path, top level first: "Munchies / Chips & Crisps". A leaf name on
      // its own is ambiguous across a platform's tree and says nothing about where the
      // shelf sits.
      col.accessor("label", {
        header: "Category",
        cell: ({ row }) => {
          const label = row.original.label || row.original.category_id;
          const parts = label.split(" / ");
          const shelf = parts.pop();
          return (
            <Link
              className="flex w-full flex-col justify-center leading-tight [&>span]:truncate text-ink no-underline hover:text-brand hover:underline underline-offset-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand"
              title={label}
              aria-label={label}
              to={`/rank/categories/${row.original.platform}/${row.original.category_id}`}
            >
              {parts.length > 0 && <span className="text-[10px] font-normal text-ink-2">{parts.join(" / ")}</span>}
              <span>{shelf}</span>
            </Link>
          );
        },
        meta: { cellClassName: "strong", width: 240, flex: false },
      }),
      // The value is a word rather than the boolean so the toolbar's faceted filter reads
      // as "tracked / off". A platform ships hundreds of shelves and an operator measures
      // a handful, so "show me the ones that are on" is the first question the table gets.
      col.accessor((row) => (row.enabled ? "tracked" : "off"), {
        id: "enabled",
        header: "Tracked",
        enableSorting: false,
        cell: ({ row }) => (
          <Switch
            aria-label={`Track ${row.original.label || row.original.category_id}`}
            checked={row.original.enabled}
            disabled={!canWrite || setCategory.isPending}
            onCheckedChange={(enabled) =>
              setCategory.mutate({
                platform: row.original.platform,
                categoryId: row.original.category_id,
                data: { enabled },
              })
            }
          />
        ),
        meta: { align: "center", width: 110, filterSelect: true, filterLabel: "category tracking", filterSegments: TRACKING_FILTERS },
      }),
      // The platform's own id path for the shelf, whatever shape it takes. On Blinkit that
      // is `l0/l1`: the top-level category id, then the subcategory id inside it -- the
      // same two levels the name path in the column before this one spells out.
      col.accessor("category_id", {
        header: "Category path",
        cell: ({ getValue }) => (
          <code title="The platform's own id path for this shelf">{getValue()}</code>
        ),
        meta: { minWidth: 140 },
      }),
      col.accessor((row) => row.health?.answered ?? NEVER, {
        id: "answered",
        header: "Answered probes",
        cell: ({ row }) => <Probes health={row.original.health} />,
        meta: { minWidth: 250 },
      }),
      col.accessor((row) => row.health?.placements ?? NEVER, {
        id: "placements",
        header: "Placements",
        cell: ({ getValue }) => <Count value={getValue()} />,
        meta: { align: "right", width: 110 },
      }),
      col.accessor((row) => row.health?.last_measured_at ?? "", {
        id: "measured",
        header: "Last measured",
        cell: ({ getValue }) => <Measured at={getValue()} />,
        meta: { width: 140 },
      }),
    ];
  }, [canWrite, setCategory]);

  return (
    <div className="flex min-w-0 flex-col gap-3.5 [&_.dt-toolbar]:px-3.5 max-sm:[&_.dt-search]:flex-[1_1_200px] max-sm:[&_.dt-search-input]:w-full">
      <PageHeader
        title="Rank"
        description="Manage the keywords and category shelves your rank jobs measure."
      />

      <Panel className="bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-3.5 py-[9px]">
          <div className="flex items-start gap-2.5">
            <Search className="mt-[3px] shrink-0 text-ink-2" size={19} aria-hidden="true" />
            <div>
              <h2 className="text-lg font-semibold">Keywords</h2>
              <p className="mt-0.5 mb-0 text-[13px] text-ink-2">One shared tracking list across every search platform.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {keywords.isSuccess && <div className="flex flex-wrap gap-4 text-xs text-ink-2 [&_strong]:font-mono [&_strong]:font-medium [&_strong]:text-ink [&_strong]:tabular-nums [&>span+span]:border-l [&>span+span]:border-line [&>span+span]:pl-4">
              <span><strong>{keywordRows.filter((row) => row.enabled).length}</strong> tracked</span>
              <span><strong>{keywordRows.length}</strong> total keywords</span>
            </div>}
            {canWrite && (
              <Dialog open={adding} onOpenChange={(open) => {
                if (create.isPending) return;
                setAdding(open);
                setTyped("");
                create.reset();
              }}>
                <DialogTrigger asChild>
                  <Button variant="outline"><Plus size={15} aria-hidden="true" /> Add keyword</Button>
                </DialogTrigger>
                <DialogContent className="gap-0 overflow-hidden border-border bg-panel p-0 sm:max-w-[400px]" showCloseButton={!create.isPending}>
                  <DialogHeader className="gap-0 border-b border-border px-5 py-4 pr-14 text-left">
                    <DialogTitle className="font-sans text-sm font-semibold leading-5">Add keyword</DialogTitle>
                    <DialogDescription className="sr-only">
                      Add a search term across all platforms.
                    </DialogDescription>
                  </DialogHeader>
                  <form className="flex flex-col" onSubmit={(event) => { event.preventDefault(); add(); }}>
                    <div className="flex flex-col gap-2 px-5 py-5">
                      <label className="text-xs font-semibold text-ink" htmlFor="rank-new-keyword">Keyword</label>
                      <Input
                        id="rank-new-keyword"
                        value={typed}
                        placeholder="e.g. chips or fruit juice"
                        disabled={create.isPending}
                        onChange={(event) => setTyped(event.currentTarget.value)}
                        aria-describedby={create.isError ? "rank-add-hint rank-add-error" : "rank-add-hint"}
                      />
                      <p id="rank-add-hint" className="m-0 text-xs leading-relaxed text-ink-2">
                        Starts untracked. Enable it from the table.
                      </p>
                    </div>
                    {create.isError && <p id="rank-add-error" role="alert" className="m-0 px-5 pb-4 text-sm text-destructive">
                      {errMsg(create.error, "Could not add the keyword. Please try again.")}
                    </p>}
                    <DialogFooter className="border-t border-border px-5 py-3">
                      <Button type="button" variant="outline" disabled={create.isPending} onClick={() => {
                        setAdding(false);
                        setTyped("");
                        create.reset();
                      }}>Cancel</Button>
                      <Button type="submit" variant="primary" disabled={!typed.trim() || create.isPending}>
                        {create.isPending ? "Adding..." : "Add keyword"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
        <Panel.Body tight>
            <DataTable
              tableKey="rank-keywords"
              rowHeight={80}
              fixedBodyHeight={keywordRows.length > 0}
              // Size from the full list, reserving the header and horizontal scrollbar.
              maxBodyHeight={keywordRows.length > 0 ? Math.min(440, keywordRows.length * 80 + 56) : 440}
              isLoading={keywords.isLoading}
              errorMessage={keywords.error ? errMsg(keywords.error, "Could not load keywords") : undefined}
              showRowCount={false}
              columns={keywordColumns}
              data={keywordRows}
              getRowId={(row) => row.keyword}
              globalPlaceholder="Search keywords..."
              csvFilename="rank-keywords"
              pinFirstColumn
              emptyMessage={keywordRows.length === 0 ? "No keywords yet. Add a keyword to start tracking." : "No keywords match your filters."}
            />
        </Panel.Body>
        <details className="border-t border-line px-3.5 py-[9px] text-xs text-ink-2 [&_p]:mt-3 [&_p]:mb-1 [&_p]:max-w-[90ch] [&_p]:leading-[1.7]">
          <summary className="w-fit cursor-pointer font-medium rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand">How keyword tracking works</summary>
          <p>Tracking applies to every search platform. Each platform column shows its own latest measurement: placements, answered / total probes, unanswered probes, and measurement time. These runs can cover different locations and times, so their figures are not combined. Not measured means no run is available; zero placements can mean an empty result or unanswered probes. Open the measurement for details.
            Each tracked keyword costs one probe per store on every search rank run.
            Case and spacing do not create a new keyword, and adding an existing term keeps its tracking setting.
            Switching tracking off pauses collection. Deleting removes the keyword from this list.
            Both actions preserve previously recorded placements.</p>
        </details>
      </Panel>

      <Panel className="bg-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-3.5 py-[9px]">
          <div className="flex items-start gap-2.5">
            <Layers className="mt-[3px] shrink-0 text-ink-2" size={19} aria-hidden="true" />
            <div>
              <h2 className="text-lg font-semibold">Categories</h2>
              <p className="mt-0.5 mb-0 text-[13px] text-ink-2">Choose the shelves to measure within each platform.</p>
            </div>
          </div>
          {categories.isSuccess && platform && <div className="flex flex-wrap gap-4 text-xs text-ink-2 [&_strong]:font-mono [&_strong]:font-medium [&_strong]:text-ink [&_strong]:tabular-nums [&>span+span]:border-l [&>span+span]:border-line [&>span+span]:pl-4">
            <span><strong>{categoryRows.filter((row) => row.enabled).length}</strong> tracked</span>
            <span><strong>{categoryRows.length}</strong> {platformLabel(platform)} shelves</span>
          </div>}
        </div>
        {platform !== undefined && <div className="flex flex-wrap items-center gap-2.5 px-3.5 pt-[9px] pb-[3px]">
          <span className="text-xs text-ink-2">Platform</span>
          <Seg
            className="max-w-full overflow-x-auto [&_button]:whitespace-nowrap"
            brand
            value={platform}
            onChange={setPlatform}
            options={categoryPlatforms.map((p) => ({ value: p, label: platformLabel(p) }))}
          />
        </div>}
        <Panel.Body tight>
          {platform === undefined ? (
            <EmptyState title="Category rank is not available">
              No platform supports category shelf measurements yet.
            </EmptyState>
          ) : (
            <DataTable
              tableKey="rank-categories"
              fixedBodyHeight
              maxBodyHeight={440}
              isLoading={categories.isLoading}
              errorMessage={categories.error ? errMsg(categories.error, "Could not load categories") : undefined}
              showRowCount={false}
              columns={categoryColumns}
              data={categoryRows}
              getRowId={(row) => row.category_id}
              globalPlaceholder="Search categories..."
              csvFilename="rank-categories"
              pinFirstColumn
              emptyMessage={categoryRows.length === 0 ? `No ${platformLabel(platform)} categories available.` : "No categories match your filters."}
            />
          )}
        </Panel.Body>
        <details className="border-t border-line px-3.5 py-[9px] text-xs text-ink-2 [&_p]:mt-3 [&_p]:mb-1 [&_p]:max-w-[90ch] [&_p]:leading-[1.7]">
          <summary className="w-fit cursor-pointer font-medium rounded-sm focus-visible:outline-2 focus-visible:outline-offset-3 focus-visible:outline-brand">How category tracking works</summary>
          <p>Categories belong to their platform and start untracked. Each enabled shelf costs one probe
            per store on every category rank run. Refreshing the category tree preserves your tracking
            choices and measurement history. A shelf that consistently returns no results may have been retired.</p>
          <p>Category paths are platform-specific identifiers shown in the table and included in CSV exports.
            To refresh the registry, run <code>qcs refresh-rank-categories</code> for the platform, then
            apply it with <code>qcs ch-migrate</code>.</p>
        </details>
      </Panel>
    </div>
  );
}
